import { type ImgHTMLAttributes } from 'react'
import {
	isMediaAsset,
	mediaAsset,
	MEDIA_ORIGIN,
	mediaUrl,
} from '#app/utils/media.ts'

const MEDIA_BREAKPOINTS = [
	320, 480, 640, 750, 828, 960, 1080, 1280, 1600, 1920, 2048,
]

type MediaFit = 'contain' | 'cover' | 'crop' | 'pad' | 'scale-down'
type MediaLayout = 'constrained' | 'fixed' | 'fullWidth'

export type MediaImageProps = Omit<
	ImgHTMLAttributes<HTMLImageElement>,
	'height' | 'src' | 'srcSet' | 'width'
> & {
	aspectRatio?: number
	breakpoints?: number[]
	fit?: MediaFit
	height?: number
	layout?: MediaLayout
	priority?: boolean
	quality?: number
	src: string
	unstyled?: boolean
	width?: number
}

function resolveMediaSource(src: string) {
	if (isMediaAsset(src)) return mediaAsset(src)
	if (/^https?:\/\//i.test(src)) return new URL(src).toString()
	return mediaUrl(src)
}

function cloudflareUrl(
	src: string,
	{
		fit,
		height,
		quality,
		width,
	}: { fit: MediaFit; height?: number; quality: number; width?: number },
) {
	if (!height && !width) return src

	const sourceUrl = new URL(src)
	const version = sourceUrl.searchParams.get('v')
	sourceUrl.search = ''

	const modifiers = [
		`fit=${height && width ? fit : 'scale-down'}`,
		'metadata=none',
		`quality=${quality}`,
		width ? `width=${width}` : null,
		height ? `height=${height}` : null,
		'f=auto',
	]
		.filter(Boolean)
		.join(',')

	const transformedUrl = new URL(
		`/cdn-cgi/image/${modifiers}/${sourceUrl.toString()}`,
		MEDIA_ORIGIN,
	)
	if (version) transformedUrl.searchParams.set('v', version)
	return transformedUrl.toString()
}

function responsiveWidths({
	breakpoints,
	layout,
	width,
}: Pick<MediaImageProps, 'breakpoints' | 'layout' | 'width'>) {
	if (breakpoints?.length) return [...new Set(breakpoints)].sort((a, b) => a - b)
	if (!width || layout === 'fullWidth') return MEDIA_BREAKPOINTS

	const maximumWidth = Math.min(width * 2, MEDIA_BREAKPOINTS.at(-1)!)
	return [
		...new Set([
			...MEDIA_BREAKPOINTS.filter((breakpoint) => breakpoint < maximumWidth),
			Math.min(width, maximumWidth),
			maximumWidth,
		]),
	].sort((a, b) => a - b)
}

export function MediaImage({
	aspectRatio,
	breakpoints,
	decoding = 'async',
	fetchPriority,
	fit = 'cover',
	height,
	layout = 'constrained',
	loading,
	priority = false,
	quality = 80,
	sizes,
	src,
	unstyled: _unstyled,
	width,
	...imageProps
}: MediaImageProps) {
	const source = resolveMediaSource(src)
	const widths = responsiveWidths({ breakpoints, layout, width })
	const srcSet = widths
		.map((candidateWidth) => {
			const candidateHeight =
				layout !== 'fullWidth' && width && height
					? Math.round((candidateWidth * height) / width)
					: undefined
			return `${cloudflareUrl(source, {
				fit,
				height: candidateHeight,
				quality,
				width: candidateWidth,
			})} ${candidateWidth}w`
		})
		.join(',\n')

	const fallbackHeight = layout === 'fullWidth' ? undefined : height
	const fallbackWidth = layout === 'fullWidth' ? undefined : width
	const fallbackSrc = cloudflareUrl(source, {
		fit,
		height: fallbackHeight,
		quality,
		width: fallbackWidth,
	})
	const responsiveSizes =
		sizes ??
		(layout === 'fixed' && width
			? `${width}px`
			: layout === 'constrained' && width
				? `(min-width: ${width}px) ${width}px, 100vw`
				: '100vw')

	return (
		<img
			{...imageProps}
			{...(priority || fetchPriority
				? { fetchpriority: priority ? 'high' : fetchPriority }
				: {})}
			decoding={decoding}
			height={fallbackHeight}
			loading={priority ? 'eager' : (loading ?? 'lazy')}
			sizes={responsiveSizes}
			src={fallbackSrc}
			srcSet={srcSet}
			width={fallbackWidth}
		/>
	)
}

export default MediaImage
